<template>
  <div class="text-white">
    <Filters
      :options="options"
      :showActionButton="true"
      :search="search"
      :resetFilter="resetFilter"
      :resetPagination="resetPagination"
    />

    <div
      v-if="isLoading"
      class="flex-grow flex flex-col items-center justify-center p-20"
    >
      <Loader />
    </div>

    <div
      v-else
      class="table-container-custom bg-white shadow-sm rounded-2xl overflow-hidden border border-1 mb-3 mt-3"
    >
      <DataTable
        :value="tableData"
        :lazy="true"
        :paginator="true"
        :rows="selectedLimit"
        :totalRecords="totalElements"
        :loading="isLoading"
        :first="(currentPage - 1) * selectedLimit"
        @page="onPage"
        @sort="onSort"
        :sortField="sortField"
        :sortOrder="sortOrder"
        :rowsPerPageOptions="[15, 25, 50, 100]"
        removableSort
        stripedRows
        scrollable
        scrollHeight="flex"
        responsiveLayout="stack"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      >
        <template #empty>
          <div class="text-center py-4 text-muted">No data found.</div>
        </template>

        <Column field="user_id" header="User ID" :sortable="true"></Column>

        <Column field="reason" header="Reason" :sortable="true"></Column>

        <Column field="note" header="Note">
          <template #body="slotProps">
            <Button
              icon="pi pi-eye"
              class="p-button-rounded p-button-text"
              @click="openModalContent(slotProps.data)"
            />
          </template>
        </Column>

        <Column field="is_active" header="Status">
          <template #body="slotProps">
            <span v-if="!slotProps.data.is_active" class="text-gray-400"
              >Banned</span
            >
            <Button
              v-else
              label="Unban"
              class="p-button-danger p-button-sm unban-cta"
              @click="unbanPlayer(slotProps.data)"
            />
          </template>
        </Column>

        <Column field="created_at" header="Created At" :sortable="true">
          <template #body="slotProps">
            {{ formatDate(slotProps.data.created_at) }}
          </template>
        </Column>

        <Column field="end_at" header="End At" :sortable="true">
          <template #body="slotProps">
            {{ formatDate(slotProps.data.end_at) }}
          </template>
        </Column>
      </DataTable>
    </div>

    <Modal
      v-if="isModalVisible"
      :isVisible="isModalVisible"
      :title="modalTitle"
      :content="contentModal"
      :closeModal="closeModal"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Filters from "@/slot/components/table/Filters.vue";
import { getFetch, postFetch } from "@/api/axiosClient";
import Swal from "sweetalert2";
import Modal from "@/slot/components/Modal/Modal.vue";
import Loader from "@/components/Loader.vue";

import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";

export default {
  name: "BlockedPlayerManager",
  components: {
    Filters,
    Modal,
    Loader,
    DataTable,
    Column,
    Button,
  },
  props: {
    skinFilt: {
      type: Number,
      required: true,
      default: 0,
    },
    category: {
      type: String,
      default: "slot",
    },
    database: {
      type: String,
      default: "theearcasino",
    },
  },
  data() {
    const today = new Date();
    const endDate = today.toISOString().split("T")[0];
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    const startDate = sevenDaysAgo.toISOString().split("T")[0];

    return {
      modalTitle: "",
      contentModal: "",
      isModalVisible: false,
      user_id: null,
      start_date: startDate,
      is_active: "All",
      end_date: endDate,
      totalPages: 0,
      isLoading: true,
      currentPage: 1,
      reasons: "All",
      totalElements: 0,

      // Stati per l'ordinamento richiesti da PrimeVue
      sortField: null,
      sortOrder: null,

      selectedLimit: 15,
      tableData: [],
      options: [
        {
          label: "User ID",
          type: "text",
          placeholder: "Search for User ID",
          property: "user_id",
        },
        {
          label: "Reason",
          type: "select",
          placeholder: "Choose Reason",
          property: "reasons",
          options: ["All", "Spam", "Offensive", "Flood", "Other"],
        },
        {
          label: "Start Date",
          type: "date",
          placeholder: "Start Date",
          property: "start_date",
          isDate: true,
        },
        {
          label: "End Date",
          type: "date",
          placeholder: "End Date",
          property: "end_date",
          isDate: true,
        },
        {
          label: "Is Active",
          type: "select",
          placeholder: "Choose Reason",
          property: "is_active",
          options: ["All", "True", "False"],
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen"]),
    startDateMax() {
      return this.end_date || new Date().toISOString().split("T")[0];
    },
    endDateMin() {
      return this.start_date || new Date(0).toISOString().split("T")[0];
    },
  },
  mounted() {
    this.search();
  },
  methods: {
    resetPagination() {
      this.currentPage = 1;
    },
    onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      this.search();
    },
    onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      this.search();
    },
    async search() {
      if (
        (this.start_date &&
          this.end_date &&
          new Date(this.start_date) > new Date(this.end_date)) ||
        !this.start_date ||
        !this.end_date
      ) {
        Swal.fire({
          icon: "error",
          title: "Invalid date range",
          text: "Make sure the dates entered are in the correct range, or are not blank.",
        });
        return;
      }

      this.isLoading = true;
      try {
        const data = await getFetch(
          `messagebanned`,
          {
            page: this.currentPage,
            limit: this.selectedLimit,
            is_active:
              this.is_active === "All"
                ? null
                : this.formatToBoolean(this.is_active),
            reason: this.reasons === "All" ? null : this.reasons,
            skin_id: this.skinFilt,
            start_date: this.start_date
              ? new Date(this.start_date).toISOString()
              : null,
            end_date: this.end_date
              ? new Date(this.end_date).toISOString()
              : null,
            user_id: this.user_id ?? null,
            // sortField: this.sortField,
          },
          true,
          this.database
        );

        this.tableData = data?.data ?? [];
        this.totalPages = data?.totalPages ?? 0;
        this.totalElements = data?.totalItems ?? 0;

        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 250);
      } catch (error) {
        console.error("Errore durante il fetch dei dati:", error);
      } finally {
        this.isLoading = false;
      }
    },
    resetFilter() {
      this.user_id = null;
      this.start_date = this.getInitialStartDate();
      this.end_date = this.getInitialEndDate();
      this.reasons = "All";
      this.currentPage = 1;
      this.is_active = "All";
      this.selectedLimit = 15;
      this.sortField = null;
      this.sortOrder = null;
      this.tableData = [];

      this.search();
    },
    getInitialStartDate() {
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      return sevenDaysAgo.toISOString().split("T")[0];
    },
    getInitialEndDate() {
      return new Date().toISOString().split("T")[0];
    },
    formatToBoolean(val) {
      return val === "True";
    },
    openModalContent(rowData) {
      this.modalTitle = "Note";
      this.contentModal = rowData.note;
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString();
    },
    async unbanPlayer(info) {
      const confirm = await Swal.fire({
        title: "Unban Player",
        text: `Are you sure you want to unban the player: "${info.user_id}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, unban",
        cancelButtonText: "Cancel",
      });

      if (confirm.isConfirmed) {
        try {
          const result = await postFetch(
            "/messagebanned/unban",
            {
              user_id: info.user_id,
              skin_id: info.skin_id,
              reason: info.reason,
            },
            true,
            this.database
          );

          if (result.status === 200) {
            Swal.fire("Unbanned!", "Player has been unbanned successfully.");
            this.resetFilter();
          } else {
            Swal.fire("Failed", "Could not unban the player.", "error");
          }
        } catch (error) {
          console.error("Unban error:", error);
          Swal.fire(
            "Error",
            "Something went wrong while unbanning the player.",
            "error",
          );
        }
      }
    },
  },
};
</script>
